import { useAutoAnimate } from '@formkit/auto-animate/react'
import { Divider, Grid, Typography, useTheme, Button, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { MdCheck } from 'react-icons/md'
import { ReactComponent as Retour } from '../assets/Retour.svg'
import { ReactComponent as WalterLearning } from '../assets/WalterLearning.svg'
import { ReactComponent as WalterSanteColor } from '../assets/WalterSanteColor.svg'
import { CallUs, Formtitle } from '../components'
import { client } from '../utils/client'
import { learningPhoneNumber, santePhoneNumber } from '../utils/constants'
import { useStripe } from '@stripe/react-stripe-js'
import { BiError, BiTimer } from 'react-icons/bi'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Elements } from '@stripe/react-stripe-js'
import { StripePublicKey } from '../utils/constants'
import Bugsnag from '@bugsnag/js'
import { NormalFormData } from 'src/utils/sharedTypes'
import { useNavigate } from 'react-router-dom'

const StripeStatus = ({ clientSecret, magicLink }: { clientSecret: string; magicLink: string }) => {
  const stripe = useStripe()
  const global_id = new URLSearchParams(location.search).get('global_id')
  const had_failed_payment = new URLSearchParams(location.search).get('had_failed_payment') === "1"
  const [message, setMessage] = useState<string>('Enregistrement de vos coordonnées bancaires en cours...')
  const [subtitle, setSubtitle] = useState<string | null>(null)
  const [status, setStatus] = useState<'processing' | 'succeeded' | 'requires_payment_method'>('processing')
  const [allowAccess, setAllowAccess] = useState(false)
  const [registrationData, setRegistrationData] = useState<NormalFormData | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const navigate = useNavigate()
  const theme = useTheme()

  useEffect(() => {
    if (global_id) {
      setIsLoadingData(true)
      client
        .get(`/registration/${global_id}/`)
        .then((resp) => {
          const data = resp.data as NormalFormData
          setRegistrationData(data)

          // Check if stripe_setup_at is null and redirect if needed
          if (!data.stripe_setup_at) {
            navigate(`/recapitulatif?global_id=${global_id}`)
          }
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
        .finally(() => {
          setIsLoadingData(false)
        })
    }
  }, [global_id, navigate])

  useEffect(() => {
    if (!stripe) {
      return
    }

    // Retrieve the SetupIntent
    stripe
      .retrieveSetupIntent(clientSecret)
      .then(({ setupIntent }) => {
        switch (setupIntent?.status) {
          case 'succeeded':
            setMessage('Vos données de paiement ont bien été mises à jour')
            if (had_failed_payment) {
              setSubtitle(
                'Le paiement en échec va être réessayé dans quelques instants, en cas de succès votre formation sera débloquée instantannément',
              )
            }
            setStatus('succeeded')
            setTimeout(() => {
              setAllowAccess(true)
            }, 5000)
            break
          case 'processing':
            setMessage('Enregistrement de vos coordonnées bancaires en cours...')
            setStatus('processing')
            break
          case 'requires_payment_method':
            setMessage(
              "Un problème est survenu lors de l'enregistrement de vos coordonnées bancaires. Veuillez essayer un autre mode de paiement",
            )
            setStatus('requires_payment_method')
            break
        }
      })
      .catch((error: Error) => {
        Bugsnag.notify(error)
      })
  })

  if (isLoadingData) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    )
  }

  if (!registrationData) {
    return (
      <Grid container spacing={3} sx={{ maxWidth: '800px', margin: '0 auto', padding: 3 }}>
        <Grid item xs={12}>
          <Typography variant="h4" color="error" gutterBottom>
            Inscription non trouvée
          </Typography>
          <Typography variant="body1">
            Impossible de trouver l'inscription correspondante. Veuillez vérifier l'URL et réessayer.
          </Typography>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Grid container justifyContent="center">
        <Grid
          item
          sx={{
            background:
              status === 'processing'
                ? theme.palette.grey[500]
                : status === 'succeeded'
                  ? theme.palette.success.main
                  : theme.palette.error.main,
            height: 80,
            width: 80,
            borderRadius: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {status === 'processing' ? (
            <BiTimer color="inherit" size="40" />
          ) : status === 'succeeded' ? (
            <MdCheck size="40" style={{ color: 'white' }} />
          ) : (
            <BiError size="40" style={{ color: 'white' }} />
          )}
        </Grid>
      </Grid>
      <Formtitle
        title={message}
        subtitle={subtitle || undefined}
        align="center"
      />
      <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button variant="contained" href={magicLink} target="_blank" disabled={magicLink === '' || !allowAccess}>
          {magicLink === '' || !allowAccess ? (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <CircularProgress color="inherit" size={20} sx={{ marginRight: '5px' }} /> Accéder à ma formation
            </div>
          ) : (
            'Accèder à ma formation'
          )}
        </Button>
      </Grid>
    </>
  )
}

const ValidatePaymentMethod = () => {
  const searchParams = new URLSearchParams(location.search)
  const stripePromise = loadStripe(StripePublicKey)
  const clientSecret = new URLSearchParams(window.location.search).get('setup_intent_client_secret') ?? ''
  const params = Object.fromEntries(searchParams.entries())

  const [magicLink, setMagicLink] = useState('')
  const [animateRef] = useAutoAnimate<HTMLDivElement>()

  const options = {
    clientSecret: clientSecret,
    appearance: {},
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      client
        .get(`/registration/${params.global_id}/magic_link/`)
        .then((resp) => {
          const data = resp.data as { magic_link: string | null }
          if (data.magic_link === null) {
            setMagicLink('')
          } else if (data.magic_link !== 'pending') {
            setMagicLink(data.magic_link)
            clearInterval(interval)
          }
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
          console.error(err)
        })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '100vh', p: 2 }} ref={animateRef}>
      <Grid item xs={12} md={6} sx={{ display: 'block', mt: { xs: 15, md: 0 } }}>
        <Elements stripe={stripePromise} options={options}>
          <StripeStatus clientSecret={clientSecret} magicLink={magicLink} />
        </Elements>
      </Grid>

      <Grid
        item
        container
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          top: 0,
          left: 0,
          pt: 2,
          pl: { xs: 2, md: 4 },
          display: 'flex',
        }}
      >
        <Grid item container sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid item>
            {location.pathname.includes('/sante') ? (
              <WalterSanteColor
                width="162px"
                onClick={() => {
                  window.open('https://walter-learning.com/sante', '_self')
                }}
              />
            ) : (
              <WalterLearning
                width="180px"
                onClick={() => {
                  window.open('https://walter-learning.com', '_self')
                }}
              />
            )}
          </Grid>
          <Grid item>
            <Divider orientation="vertical" sx={{ height: 40, mx: 2 }} />
          </Grid>
          <Grid
            item
            sx={{
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => {
              location.pathname.includes('/sante')
                ? window.open('https://walter-learning.com/sante', '_self')
                : window.open('https://walter-learning.com', '_self')
            }}
          >
            <Retour />
            <Typography variant="body1" sx={{ ml: 1 }}>
              Retour au site
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          mt: 2,
          mr: 4,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <CallUs phone={window.location.href.includes('sante') ? santePhoneNumber : learningPhoneNumber} />
      </Grid>
    </Grid>
  )
}

export default ValidatePaymentMethod
